import Image, { ImageProps } from 'next/image';

interface StoryblokAsset {
    id?: number;
    alt?: string;
    name?: string;
    focus?: string;
    title?: string;
    source?: string;
    filename: string;
    copyright?: string;
    fieldtype?: string;
    meta_data?: Record<string, unknown>;
    is_external_url?: boolean;
}

interface StoryblokImageProps extends Omit<ImageProps, 'src' | 'alt'> {
    src: string | StoryblokAsset;
    alt?: string;
    width: number;
    height: number;
    quality?: number;
    format?: 'webp' | 'avif' | 'jpg' | 'png' | 'auto' | 'svg';
    smart?: boolean;
    fitIn?: boolean;
    priority?: boolean;
    focus?: string;
    filters?: {
        blur?: number;
        brightness?: number;
        fill?: string;
        grayscale?: boolean;
        rotate?: number;
    };
}

function buildStoryblokImageUrl(
    filename: string,
    width: number,
    height: number,
    quality: number,
    format: string,
    smart: boolean,
    fitIn: boolean,
    focus?: string,
    filters?: StoryblokImageProps['filters'],
): string {
    const isSvg = filename.toLowerCase().endsWith('.svg');

    if (isSvg) {
        return filename;
    }

    const baseUrl = filename.split('/m/')[0];
    const params: string[] = [];

    if (fitIn) {
        params.push(`/m/fit-in/${width}x${height}`);
    } else {
        params.push(`/m/${width}x${height}`);
    }

    if (smart) {
        params.push('/smart');
    }

    const filterList: string[] = [];

    if (focus && !smart) {
        filterList.push(`focal(${focus})`);
    }


    if (format === 'auto') {
        filterList.push('format(webp)');
    } else {
        filterList.push(`format(${format})`);
    }

    filterList.push(`quality(${quality})`);

    if (filters) {
        if (filters.blur !== undefined) {
            filterList.push(`blur(${filters.blur})`);
        }
        if (filters.brightness !== undefined) {
            filterList.push(`brightness(${filters.brightness})`);
        }
        if (filters.fill) {
            filterList.push(`fill(${filters.fill})`);
        }
        if (filters.grayscale) {
            filterList.push('grayscale()');
        }
        if (filters.rotate !== undefined) {
            filterList.push(`rotate(${filters.rotate})`);
        }
    }

    // When using fit-in, add transparent background if no fill is specified
    if (fitIn && (!filters || !filters.fill)) {
        filterList.push('fill(transparent)');
    }

    if (filterList.length > 0) {
        params.push(`/filters:${filterList.join(':')}`);
    }

    return `${baseUrl}${params.join('')}`;
}

export default function StoryblokImage({
    src,
    alt,
    width,
    height,
    quality = 80,
    format = 'auto',
    smart = false,
    fitIn = false,
    focus,
    priority = false,
    fetchPriority,
    filters,
    ...props
}: StoryblokImageProps) {
    const imageData = typeof src === 'string'
        ? { filename: src, alt: alt || '' }
        : { ...src, alt: src.alt || alt || '' };

    const { filename, alt: imageAlt, focus: assetFocus } = imageData;
    const finalFocus = focus || assetFocus;

    const optimizedSrc = buildStoryblokImageUrl(
        filename,
        width,
        height,
        quality,
        format,
        smart,
        fitIn,
        finalFocus,
        filters
    );

    return (
        <Image
            src={optimizedSrc}
            alt={imageAlt}
            width={width}
            height={height}
            priority={priority}
            fetchPriority={fetchPriority}
            unoptimized
            {...props}
        />
    );
}
